import { THit } from '@/types';
import { useEffect, useState } from 'react';

export interface ISearchResult {
	hits: THit[];
	total: number;
	page: number;
	totalPages: number;
}

export interface IAutocompleteCategory {
	_id: string;
	displayName: string;
	slug: string;
	platform: string;
}

const emptySearchResult: ISearchResult = {
	hits: [],
	total: 0,
	page: 1,
	totalPages: 0,
};

export function useSearch({
	q,
	platform,
	page,
	initialQuery,
	initialData,
	skip = false,
}: {
	q: string;
	platform?: string;
	page: number;
	initialQuery?: string;
	initialData?: ISearchResult;
	skip?: boolean;
}): ISearchResult & { loading: boolean } {
	const matchesInitialData = (): boolean =>
		!!initialData && q === (initialQuery ?? '') && page === initialData.page;

	const [result, setResult] = useState<ISearchResult>(
		matchesInitialData() ? (initialData as ISearchResult) : emptySearchResult
	);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (skip) {
			return;
		}

		if (matchesInitialData()) {
			setResult(initialData as ISearchResult);
			setLoading(false);
			return;
		}

		const controller = new AbortController();
		setLoading(true);

		const timeout = setTimeout(async () => {
			try {
				const params = new URLSearchParams({ q, page: String(page) });
				if (platform) {
					params.set('platform', platform);
				}
				const res = await fetch(`/api/search?${params.toString()}`, {
					signal: controller.signal,
				});
				const data = await res.json();
				setResult(data);
				setLoading(false);
			} catch (e: any) {
				if (e?.name !== 'AbortError') {
					setLoading(false);
				}
			}
		}, 250);

		return () => {
			clearTimeout(timeout);
			controller.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [q, platform, page, skip]);

	return { ...result, loading };
}

const emptyAutocomplete = {
	apps: [] as Partial<THit>[],
	totalApps: 0,
	categories: [] as IAutocompleteCategory[],
	totalCategories: 0,
};

export function useAutocomplete(
	q: string,
	platform?: string
): typeof emptyAutocomplete & { loading: boolean } {
	const [result, setResult] = useState(emptyAutocomplete);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		const query = q.trim();
		if (!query) {
			setResult(emptyAutocomplete);
			setLoading(false);
			return;
		}

		const controller = new AbortController();
		setLoading(true);

		const timeout = setTimeout(async () => {
			try {
				const params = new URLSearchParams({ q: query });
				if (platform) {
					params.set('platform', platform);
				}
				const res = await fetch(`/api/autocomplete?${params.toString()}`, {
					signal: controller.signal,
				});
				const data = await res.json();
				setResult(data);
				setLoading(false);
			} catch (e: any) {
				if (e?.name !== 'AbortError') {
					setLoading(false);
				}
			}
		}, 200);

		return () => {
			clearTimeout(timeout);
			controller.abort();
		};
	}, [q, platform]);

	return { ...result, loading };
}
